import { useState, useEffect, useMemo } from "react";
import { jsPDF } from "jspdf";
import { Info, Calculator, FileDown, Save, Trash2, AlertTriangle, Settings2, ChevronDown, Loader2, X, Package, GitCompare } from "lucide-react";
import { api } from "../../../lib/api";

export const ease = [0.16, 1, 0.3, 1];
export const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
export const U = { mm: 0.1, cm: 1, m: 100, ft: 30.48, in: 2.54 };
export const toCm = (v, u) => num(v) * (U[u] || 1);
export const toM = (v, u) => toCm(v, u) / 100;
export const m2 = (n) => `${n.toFixed(2)} m²`;
export const ml = (n) => `${n.toFixed(2)} m`;
export const money = (n) => `$${(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const ceilStep = (v, step) => step > 0 ? Math.ceil(v / step - 1e-9) * step : v;
export const minSale = (v, min) => (v > 0 ? Math.max(v, min) : 0);
export const UNITS = [["mm", "Milímetros"], ["cm", "Centímetros"], ["m", "Metros"], ["ft", "Pies"], ["in", "Pulgadas"]];
export const UNITS_BASIC = [["mm", "Milímetros"], ["cm", "Centímetros"], ["m", "Metros"]];
export const DESP_OPTS = [["0", "Sin desperdicio"], ["3", "3%"], ["5", "5%"], ["8", "8%"], ["10", "10%"], ["12", "12%"], ["15", "15%"], ["18", "18%"], ["20", "20%"]];
export const ROLLOS = ["140", "150", "160", "180", "280", "300", "320"];
export const FACTORES = ["1.50", "2.00", "2.20", "2.50", "3.00"];
export const REDONDEOS = [["0", "Exacto"], ["0.25", "Al siguiente 25 cm"], ["0.50", "Al siguiente 50 cm"], ["1", "Al siguiente metro"]];
export const soportes = (lenM) => lenM <= 0 ? 0 : lenM <= 2 ? 2 : lenM <= 4 ? 3 : 3 + Math.ceil((lenM - 4) / 1.5);
export const S_DEFAULT = {
  desperdicio_default: 10, venta_min_tela: 1, venta_min_area: 1,
  precio_tela_m: 0, precio_confeccion: 0, precio_instalacion: 0,
  precio_persiana_m2: 0, precio_recubrimiento_m2: 0, margen_pct: 0, iva_pct: 16,
  carros_cada_cm: 8, ojillos_cada_cm: 15, adhesivo_rend: 5,
};
export const sv = (s, k) => { const n = (s || S_DEFAULT)[k]; return n === undefined || n === null ? "" : String(n); };
export const svPrice = (s, k) => { const n = (s || S_DEFAULT)[k]; return n ? String(n) : ""; };

export function Field({ label, value, onChange, type = "number", step = "0.01", min = "0", suffix, testid, children }) {
  return (
    <label className="block">
      <span className="text-xs text-navy/50 mb-1 block">{label}</span>
      {children || (
        <div className="relative">
          <input type={type} step={step} min={min} value={value} onChange={(e) => onChange(e.target.value)}
            className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet transition-colors" data-testid={testid} />
          {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-navy/35">{suffix}</span>}
        </div>
      )}
    </label>
  );
}
export const Sel = ({ label, value, onChange, opts, testid }) => (
  <Field label={label}>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet" data-testid={testid}>
      {opts.map((o) => Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>)}
    </select>
  </Field>
);
export function HowTo({ title, children }) {
  return (
    <div className="bg-white border border-navy/10 rounded-xl p-5" data-testid="howto">
      <div className="flex items-center gap-2 overline text-navy/40 mb-3"><Info className="w-3.5 h-3.5" /> {title}</div>
      <div className="space-y-2 text-sm text-navy/70 leading-relaxed">{children}</div>
    </div>
  );
}
export function Advanced({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-navy/10 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3 text-sm text-navy/60 hover:bg-navy/[0.02]" data-testid="calc-ajustes">
        <span className="flex items-center gap-2"><Settings2 className="w-3.5 h-3.5" /> Ajustes avanzados (configurables)</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 grid sm:grid-cols-2 gap-4 border-t border-navy/5 pt-4">{children}</div>}
    </div>
  );
}
function ResultRow({ label, value, strong }) {
  return (
    <div className={`flex justify-between items-baseline gap-3 py-2 border-b border-navy/5 last:border-0 ${strong ? "text-navy" : "text-navy/70"}`}>
      <span className={`text-sm ${strong ? "font-medium" : "text-navy/50"}`}>{label}</span>
      <span className={`${strong ? "text-lg font-medium" : "text-sm"} tabular-nums text-right`}>{value}</span>
    </div>
  );
}
export function exportPDF(title, rows) {
  const doc = new jsPDF();
  doc.setFontSize(16); doc.text(title, 14, 18);
  doc.setFontSize(9); doc.setTextColor(120);
  doc.text(`GOÛT Studio · ${new Date().toLocaleString("es-MX")}`, 14, 25);
  doc.setTextColor(20); doc.setFontSize(10);
  let y = 36;
  rows.forEach((r) => {
    if (y > 275) { doc.addPage(); y = 20; }
    if (r.section) { doc.setFont(undefined, "bold"); doc.text(r.section, 14, y); doc.setFont(undefined, "normal"); y += 7; }
    else { doc.text(String(r.label), 14, y); doc.text(String(r.value), 196, y, { align: "right" }); y += 6; }
  });
  doc.save(`${title.replace(/[^\w]+/g, "-")}.pdf`);
}
export function CutDiagram({ rollCm, pieces, label = "Acomodo sobre el rollo" }) {
  if (!rollCm || !pieces?.length) return null;
  const scale = 260 / Math.max(rollCm, 1);
  let yCursor = 0; const rects = [];
  pieces.forEach((p, i) => {
    const perRow = Math.max(1, Math.floor(rollCm / p.w));
    let placed = 0;
    while (placed < p.count && rects.length < 400) {
      const inRow = Math.min(perRow, p.count - placed);
      for (let c = 0; c < inRow; c++) rects.push({ x: c * p.w * scale, y: yCursor * scale, w: p.w * scale - 1, h: p.h * scale - 1, color: i });
      placed += inRow; yCursor += p.h;
    }
  });
  const H = Math.min(340, Math.max(70, yCursor * scale));
  const colors = ["#7c3aed", "#0ea5e9", "#f59e0b", "#10b981"];
  return (
    <div className="mt-4" data-testid="cut-diagram">
      <div className="overline text-navy/40 mb-2">{label} ({rollCm} cm de ancho)</div>
      <svg width="100%" viewBox={`0 0 262 ${H + 2}`} className="border border-navy/10 rounded-lg bg-navy/[0.02]">
        {rects.map((r, i) => (
          <rect key={i} x={r.x + 1} y={r.y + 1} width={Math.max(2, r.w)} height={Math.max(2, r.h)} rx="2"
            fill={colors[r.color % 4]} fillOpacity="0.35" stroke={colors[r.color % 4]} strokeWidth="0.7" />
        ))}
      </svg>
    </div>
  );
}

// Reusable area capture table: rows of {nombre, largo, ancho, cant}
export function AreaTable({ title, rows, setRows, u, addLabel = "+ Área", defaultName = "Área", testid }) {
  const set = (i, k, v) => setRows(rows.map((x, j) => j === i ? { ...x, [k]: v } : x));
  return (
    <div className="bg-white border border-navy/10 rounded-xl p-5 space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between"><span className="overline text-navy/40">{title}</span>
        <button onClick={() => setRows([...rows, { nombre: `${defaultName} ${rows.length + 1}`, largo: "", ancho: "", cant: "1" }])} className="btn btn-ghost text-xs" data-testid={`${testid}-add`}>{addLabel}</button></div>
      {rows.map((a, i) => (
        <div key={i} className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.7fr)_auto] gap-2 items-center">
          <input value={a.nombre} onChange={(e) => set(i, "nombre", e.target.value)} placeholder="Nombre" className="min-w-0 bg-white border border-navy/10 rounded-lg px-2.5 py-2 text-sm" />
          <input type="number" value={a.largo} onChange={(e) => set(i, "largo", e.target.value)} placeholder="Largo" className="min-w-0 bg-white border border-navy/10 rounded-lg px-2.5 py-2 text-sm" data-testid={`${testid}-largo-${i}`} />
          <input type="number" value={a.ancho} onChange={(e) => set(i, "ancho", e.target.value)} placeholder="Ancho/Alto" className="min-w-0 bg-white border border-navy/10 rounded-lg px-2.5 py-2 text-sm" data-testid={`${testid}-ancho-${i}`} />
          <input type="number" value={a.cant} onChange={(e) => set(i, "cant", e.target.value)} placeholder="×" className="min-w-0 bg-white border border-navy/10 rounded-lg px-2.5 py-2 text-sm" />
          <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="shrink-0 text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      {rows.length === 0 && <p className="text-xs text-navy/40">Sin filas. Agrega una para continuar.</p>}
    </div>
  );
}
export const sumArea = (rows, u) => rows.reduce((s, a) => s + toM(a.largo, u) * toM(a.ancho, u) * Math.max(1, Math.round(num(a.cant)) || 1), 0);
export const sumPerim = (rows, u) => rows.reduce((s, a) => s + 2 * (toM(a.largo, u) + toM(a.ancho, u)) * Math.max(1, Math.round(num(a.cant)) || 1), 0);

// Product catalog hook + selector (inventory-aware)
export function useCatalog(modality) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/gout/calculator/catalog?modality=${modality}`).then((r) => setItems(r.data)).catch(() => {}); }, [modality]);
  return items;
}
export function ProductSelect({ items, value, onChange, testid }) {
  if (!items?.length) return null;
  return (
    <Field label="Producto del catálogo (opcional)">
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet" data-testid={testid || "calc-product"}>
        <option value="" suppressHydrationWarning>— Manual (sin catálogo) —</option>
        {items.map((p) => <option key={p.id} value={p.id} suppressHydrationWarning>{p.name} · {money(p.price)}/{p.unit} · stock {p.stock}</option>)}
      </select>
    </Field>
  );
}

function SaveDialog({ open, onClose, onConfirm, defaultTitle, saving }) {
  const [title, setTitle] = useState(defaultTitle);
  const [client, setClient] = useState("");
  useEffect(() => { if (open) { setTitle(defaultTitle); setClient(""); } }, [open, defaultTitle]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4" onClick={onClose} data-testid="calc-save-dialog">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-lg">Guardar cotización</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <Field label="Título de la cotización" value={title} onChange={setTitle} type="text" step={undefined} min={undefined} testid="calc-save-title" />
          <Field label="Cliente (opcional)" value={client} onChange={setClient} type="text" step={undefined} min={undefined} testid="calc-save-client" />
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn btn-ghost text-sm">Cancelar</button>
          <button onClick={() => onConfirm({ title: title.trim() || defaultTitle, client: client.trim() })} disabled={saving}
            className="btn bg-gradient-violet text-white text-sm" data-testid="calc-save-confirm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

export function ResultCard({ title, modality, rows, warnings, diagram, total, inputs, onSave, onCompare, compareLabel }) {
  const [dlg, setDlg] = useState(false);
  const [saving, setSaving] = useState(false);
  const confirm = async (meta) => {
    setSaving(true);
    try { await onSave({ ...meta, modality, rows, total, inputs }); setDlg(false); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-gradient-to-br from-white to-cream border border-navy/10 rounded-xl p-6 h-fit" data-testid="calc-result">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 overline text-navy/40"><Calculator className="w-3.5 h-3.5" /> Resultado</div>
        <div className="flex gap-2">
          {onCompare && <button onClick={onCompare} className="btn btn-ghost text-xs" data-testid="calc-compare"><GitCompare className="w-3.5 h-3.5" /> {compareLabel || "Comparar"}</button>}
          <button onClick={() => exportPDF(title, rows)} className="btn btn-ghost text-xs" data-testid="calc-pdf"><FileDown className="w-3.5 h-3.5" /> PDF</button>
          <button onClick={() => setDlg(true)} className="btn btn-ghost text-xs" data-testid="calc-save"><Save className="w-3.5 h-3.5" /> Guardar</button>
        </div>
      </div>
      {(warnings || []).length > 0 && (
        <div className="mb-3 space-y-1.5" data-testid="calc-warnings">
          {warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {w}
            </div>
          ))}
        </div>
      )}
      {rows.map((r, i) => (
        r.section
          ? <div key={i} className="overline text-navy/35 mt-3 mb-1">{r.section}</div>
          : <ResultRow key={i} label={r.label} value={r.value} strong={r.strong} />
      ))}
      {diagram}
      <SaveDialog open={dlg} onClose={() => setDlg(false)} onConfirm={confirm} defaultTitle={title} saving={saving} />
    </div>
  );
}

// Layout wrapper for a calculator (left form column + right result)
export function CalcLayout({ children }) {
  return <div className="grid lg:grid-cols-2 gap-6">{children}</div>;
}
export function FormCol({ children }) {
  return <div className="space-y-5">{children}</div>;
}
export function Card({ children, testid }) {
  return <div className="bg-white border border-navy/10 rounded-xl p-5 grid sm:grid-cols-2 gap-4" data-testid={testid}>{children}</div>;
}
